import { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Box,
  Collapse,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  MdDashboard,
  MdSubscriptions,
  MdPeople,
  MdAttachMoney,
  MdBarChart,
  MdHistory,
  MdMenu,
  MdClose,
  MdCardMembership,
  MdAccessTime,
  MdPayment,
  MdExpandMore,
  MdExpandLess,
  MdExtension,
} from "react-icons/md";
import Header from "../../Header";

const drawerWidth = 260;

const menuItems = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: MdDashboard,
    path: "/subscritionDashboard",
  },
  {
    id: "subscriptions",
    label: "Subscriptions",
    icon: MdSubscriptions,
    children: [
      { id: "plans", label: "Plans", path: "plans", icon: MdCardMembership },
      { id: "addons", label: "Add-ons", path: "addons", icon: MdExtension },
      // { id: "features", label: "Features", path: "features", icon: MdStars },
      { id: "trials", label: "Trials", path: "trials", icon: MdAccessTime },
      {
        id: "service-fees",
        label: "Service Fees",
        path: "service-fees",
        icon: MdPayment,
      },
    ],
  },
  { id: "users", label: "Users", icon: MdPeople, path: "users" },
  { id: "billing", label: "Billing", icon: MdAttachMoney, path: "billing" },
  { id: "analytics", label: "Analytics", icon: MdBarChart, path: "analytics" },
  {
    id: "audit-logs",
    label: "Audit Logs",
    icon: MdHistory,
    path: "audit-logs",
  },
];

export default function SubscribtionDashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState(["subscriptions"]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const toggleExpandMenu = (id) => {
    setExpandedMenus((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const getBreadcrumbs = () => {
    const path = location.pathname;
    if (path === "/") return ["Dashboard"];
    const parts = path.split("/").filter(Boolean);
    return parts.map(
      (part) => part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, " "),
    );
  };

  const getPageTitle = () => {
    const breadcrumbs = getBreadcrumbs();
    return breadcrumbs[breadcrumbs.length - 1] || "Dashboard";
  };

  const drawer = (
    <div
      className="h-full flex flex-col"
      style={{ backgroundColor: "#FFFFFF" }}
    >
      {isMobile && (
        <Box className="flex items-center justify-between p-4 border-b">
          <Typography variant="h6" className="font-semibold text-primary">
            Menu
          </Typography>
          <IconButton onClick={handleDrawerToggle}>
            <MdClose size={24} />
          </IconButton>
        </Box>
      )}

      <List className="flex-1 py-2 md:py-4 overflow-y-auto">
        {menuItems.map((item) => (
          <div key={item.id}>
            {item.children ? (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => toggleExpandMenu(item.id)}
                    className="mx-2 rounded-lg"
                    sx={{
                      color: "#64748B",
                      "&:hover": {
                        backgroundColor: "#F8FAFC",
                      },
                      py: { xs: 1.5, md: 1 },
                    }}
                  >
                    <ListItemIcon
                      sx={{ minWidth: { xs: 44, md: 40 }, color: "#64748B" }}
                    >
                      <item.icon size={isMobile ? 22 : 20} />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: { xs: 15, md: 14 },
                        fontWeight: 500,
                      }}
                    />
                    {expandedMenus.includes(item.id) ? (
                      <MdExpandLess size={isMobile ? 22 : 20} />
                    ) : (
                      <MdExpandMore size={isMobile ? 22 : 20} />
                    )}
                  </ListItemButton>
                </ListItem>
                <Collapse
                  in={expandedMenus.includes(item.id)}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    {item.children.map((child) => (
                      <ListItem key={child.id} disablePadding>
                        <ListItemButton
                          onClick={() => handleMenuItemClick(child.path)}
                          className="mx-2 rounded-lg pl-8 md:pl-12"
                          selected={location.pathname === child.path}
                          sx={{
                            color: "#64748B",
                            "&:hover": {
                              backgroundColor: "#F8FAFC",
                            },
                            "&.Mui-selected": {
                              backgroundColor: "rgba(31,79,255,0.08)",
                              color: "#1F4FFF",
                              "&:hover": {
                                backgroundColor: "rgba(31,79,255,0.12)",
                              },
                            },
                            py: { xs: 1.5, md: 1 },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: { xs: 36, md: 32 },
                              color: "inherit",
                            }}
                          >
                            <child.icon size={isMobile ? 20 : 18} />
                          </ListItemIcon>
                          <ListItemText
                            primary={child.label}
                            primaryTypographyProps={{
                              fontSize: { xs: 14, md: 13 },
                              fontWeight: 500,
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </>
            ) : (
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleMenuItemClick(item.path)}
                  className="mx-2 rounded-lg"
                  selected={location.pathname === item.path}
                  sx={{
                    color: "#64748B",
                    "&:hover": {
                      backgroundColor: "#F8FAFC",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "rgba(31,79,255,0.08)",
                      color: "#1F4FFF",
                      "&:hover": {
                        backgroundColor: "rgba(31,79,255,0.12)",
                      },
                    },
                    py: { xs: 1.5, md: 1 },
                  }}
                >
                  <ListItemIcon
                    sx={{ minWidth: { xs: 44, md: 40 }, color: "inherit" }}
                  >
                    <item.icon size={isMobile ? 22 : 20} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: { xs: 15, md: 14 },
                      fontWeight: 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            )}
          </div>
        ))}
      </List>
    </div>
  );

  return (
    <Box className="h-screen flex flex-col">
      <Header />
      {isMobile && (
        <Box
          className="flex items-center justify-between px-4 py-2 bg-white border-b"
          sx={{ bgcolor: "#FFFFFF" }}
        >
          <IconButton onClick={handleDrawerToggle} sx={{ color: "#64748B" }}>
            <MdMenu size={24} />
          </IconButton>
          <Typography variant="h6" className="font-semibold">
            {getPageTitle()}
          </Typography>
          <Box width={40} />
        </Box>
      )}

      <Box
        className="flex flex-1 overflow-hidden"
        sx={{
          bgcolor: "#F8FAFC",
          px: { xs: 0, md: 8 },
          py: { xs: 0, md: 4 },
        }}
      >
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              border: "none",
              position: "relative",
              height: "100%",
              padding: 0,
            },
          }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              border: "none",
              padding: 0,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Box className="flex-1 overflow-auto">
          <Box className="max-w-[1280px] mx-auto px-3 sm:px-3 md:px-0 md:pl-5 py-4">
            <Outlet />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
